import React, { useEffect, useRef, useState, useCallback } from "react";
import apiRoutes from "../../../api/apiRoutes";
// import { authHeaders } from '../../../utils/auth';
import { apiFetchJson } from "../../../utils/http";

/**
 * Filtro lateral — autocompletes condicionados por grupo/estado.
 * - Nombre/Documento: siguen usando /clientes/list (LIKE server-side).
 * - Cod. Cliente: ahora usa /clientes/autocomplete?tipo=codigo&q=<term> con scroll infinito por hasMore.
 */
export default function FiltroClientes({
  initialFilters = {},
  onSubmit = () => {},
  onClose = () => {},
  onChangeFilters = null, // para aplicar en vivo al eliminar chips (excepto grupos)
}) {
  const filtroRef = useRef(null);

  // util
  const PAGE_SIZE = 10;
  const dedupBy = (arr, keyFn) => {
    const seen = new Set();
    return arr.filter((it) => {
      const k = keyFn(it);
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  };

  // ---------- Estados de búsqueda / chips ----------
  // Nombre
  const [nombreQ, setNombreQ] = useState("");
  const [nombreItems, setNombreItems] = useState([]);
  const [selectedNombres, setSelectedNombres] = useState([]);
  const [nombreLoading, setNombreLoading] = useState(false);
  const [nombreOpen, setNombreOpen] = useState(false);
  const [nombrePage, setNombrePage] = useState(0);
  const [nombreHasMore, setNombreHasMore] = useState(false);

  // Documento
  const [documentoQ, setDocumentoQ] = useState("");
  const [documentoItems, setDocumentoItems] = useState([]);
  const [selectedDocumentos, setSelectedDocumentos] = useState([]);
  const [documentoLoading, setDocumentoLoading] = useState(false);
  const [documentoOpen, setDocumentoOpen] = useState(false);
  const [documentoPage, setDocumentoPage] = useState(0);
  const [documentoHasMore, setDocumentoHasMore] = useState(false);

  // Cod. Cliente (multi)
  const [codigoClienteQ, setCodigoClienteQ] = useState("");
  const [codigoClienteItems, setCodigoClienteItems] = useState([]);
  const [selectedCodigos, setSelectedCodigos] = useState([]);
  const [codigoClienteLoading, setCodigoClienteLoading] = useState(false);
  const [codigoClienteOpen, setCodigoClienteOpen] = useState(false);
  const [codigoPage, setCodigoPage] = useState(0);
  const [codigoHasMore, setCodigoHasMore] = useState(false);

  // --------- Grupo Cliente (autocomplete + chips) ---------
  const [grupoQ, setGrupoQ] = useState("");
  const [grupoItems, setGrupoItems] = useState([]); // [{id, nombre}]
  const [grupoOpen, setGrupoOpen] = useState(false);
  const [grupoLoading, setGrupoLoading] = useState(false);
  const [grupoPage, setGrupoPage] = useState(0);
  const [grupoHasMore, setGrupoHasMore] = useState(false);
  const [selectedGrupos, setSelectedGrupos] = useState([]); // [{id, nombre}]
  const [errorGrupos, setErrorGrupos] = useState("");

  // Estado (dinámico desde backend)
  const [estados, setEstados] = useState([{ id: "", label: "Todos" }]);
  const [estadoId, setEstadoId] = useState("");

  const [error, setError] = useState("");
  const [errorEstados, setErrorEstados] = useState("");

  // ---------- helpers ----------
  // Cierra dropdowns (deja abierto solo el indicado en `except`)
  const closeDropdowns = (except = null) => {
    if (except !== "nombre") setNombreOpen(false);
    if (except !== "documento") setDocumentoOpen(false);
    if (except !== "codigo") setCodigoClienteOpen(false);
    if (except !== "grupo") setGrupoOpen(false);
  };
  const handleKeyDown = (e) => {
    if (e.key === "Escape") closeDropdowns(null);
  };

  // Snapshot COMPLETO (incluye grupos) — para Submit
  const buildSnapshot = (nombresArr, documentosArr, codigosArr, gruposArr) => {
    const codigosCliente = Array.from(
      new Set(codigosArr.map((i) => i.codigo).filter(Boolean)),
    );
    const codigosExtra = Array.from(
      new Set([
        ...nombresArr.map((i) => i.codigo).filter(Boolean),
        ...documentosArr.map((i) => i.codigo).filter(Boolean),
      ]),
    );

    const snap = {
      nombresObjs: nombresArr.map(({ id, nombre, documento, codigo }) => ({
        id,
        nombre,
        documento,
        codigo,
      })),
      documentosObjs: documentosArr.map(
        ({ id, nombre, documento, codigo }) => ({
          id,
          nombre,
          documento,
          codigo,
        }),
      ),
      nombres: nombresArr.map((i) => i.nombre),
      documentos: documentosArr.map((i) => i.documento),
      codigosCliente,
      codigosExtra,
      grupoObjs: (gruposArr ?? []).map(({ id, nombre }) => ({ id, nombre })),
      grupoIds: (gruposArr ?? []).map((g) => g.id),
    };

    if (snap.grupoIds.length === 1) {
      snap.grupoId = snap.grupoIds[0];
      snap.grupoNombre = gruposArr[0]?.nombre || "";
    }
    return snap;
  };

  // Snapshot LIGERO (sin grupos) — para emisiones “en vivo”
  const buildLiveSnapshot = (nombresArr, documentosArr, codigosArr) => {
    const codigosCliente = Array.from(
      new Set(codigosArr.map((i) => i.codigo).filter(Boolean)),
    );
    const codigosExtra = Array.from(
      new Set([
        ...nombresArr.map((i) => i.codigo).filter(Boolean),
        ...documentosArr.map((i) => i.codigo).filter(Boolean),
      ]),
    );

    return {
      nombresObjs: nombresArr.map(({ id, nombre, documento, codigo }) => ({
        id,
        nombre,
        documento,
        codigo,
      })),
      documentosObjs: documentosArr.map(
        ({ id, nombre, documento, codigo }) => ({
          id,
          nombre,
          documento,
          codigo,
        }),
      ),
      nombres: nombresArr.map((i) => i.nombre),
      documentos: documentosArr.map((i) => i.documento),
      codigosCliente,
      codigosExtra,
    };
  };

  // 👉 Emisión “en vivo” SOLO al sacar chips de nombre/doc/código (sin grupos)
  const emitAllFrom = (nombresArr, documentosArr, codigosArr) => {
    if (typeof onChangeFilters !== "function") return;
    const snap = buildLiveSnapshot(nombresArr, documentosArr, codigosArr);
    if (estadoId !== "") snap.estadoId = estadoId;
    onChangeFilters(snap);
  };

  // ---------- Re-hidratación ----------
  useEffect(() => {
    const nombresObjs = Array.isArray(initialFilters.nombresObjs)
      ? initialFilters.nombresObjs
      : Array.isArray(initialFilters.nombres)
        ? initialFilters.nombres.map((n) => ({ id: n, nombre: n }))
        : initialFilters.nombre
          ? [{ id: initialFilters.nombre, nombre: initialFilters.nombre }]
          : [];

    const documentosObjs = Array.isArray(initialFilters.documentosObjs)
      ? initialFilters.documentosObjs
      : Array.isArray(initialFilters.documentos)
        ? initialFilters.documentos.map((d) => ({ id: d, documento: d }))
        : initialFilters.documento
          ? [
              {
                id: initialFilters.documento,
                documento: initialFilters.documento,
              },
            ]
          : [];

    const codigosCliente = Array.isArray(initialFilters.codigosCliente)
      ? initialFilters.codigosCliente
      : initialFilters.codigo
        ? [initialFilters.codigo]
        : [];

    const gruposIniciales = Array.isArray(initialFilters.grupoObjs)
      ? initialFilters.grupoObjs
      : Array.isArray(initialFilters.grupoIds)
        ? initialFilters.grupoIds.map((id) => ({
            id,
            nombre: initialFilters.grupoNombre || String(id),
          }))
        : initialFilters.grupoId != null
          ? [
              {
                id: Number(initialFilters.grupoId),
                nombre:
                  initialFilters.grupoNombre || String(initialFilters.grupoId),
              },
            ]
          : [];

    setSelectedNombres(
      nombresObjs.map((o) => ({
        id: o.id ?? o.codigo ?? o.nombre,
        nombre: o.nombre,
        documento: o.documento,
        codigo: o.codigo,
      })),
    );

    setSelectedDocumentos(
      documentosObjs.map((o) => ({
        id: o.id ?? o.codigo ?? o.documento,
        nombre: o.nombre,
        documento: o.documento,
        codigo: o.codigo,
      })),
    );

    setSelectedCodigos(codigosCliente.map((c) => ({ codigo: c })));
    setSelectedGrupos(
      gruposIniciales.map((g) => ({ id: g.id, nombre: g.nombre })),
    );

    // reset dropdowns/paginación
    setNombreQ("");
    setDocumentoQ("");
    setCodigoClienteQ("");
    setGrupoQ("");
    setNombreOpen(false);
    setDocumentoOpen(false);
    setCodigoClienteOpen(false);
    setGrupoOpen(false);
    setNombreItems([]);
    setDocumentoItems([]);
    setCodigoClienteItems([]);
    setGrupoItems([]);
    setNombrePage(0);
    setDocumentoPage(0);
    setCodigoPage(0);
    setGrupoPage(0);
    setNombreHasMore(false);
    setDocumentoHasMore(false);
    setCodigoHasMore(false);
    setGrupoHasMore(false);

    // estado
    setEstadoId(initialFilters.estadoId ?? "");
  }, [initialFilters]);

  // ---------- Cerrar panel al hacer click fuera ----------
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filtroRef.current && !filtroRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // ---------- Fetchers ----------
  // Nombre/Documento: vía /clientes/list (LIKE)
  const fetchNombrePage = useCallback(
    async (term, page, append = false) => {
      try {
        setNombreLoading(true);
        const params = new URLSearchParams({
          page,
          size: PAGE_SIZE,
          nombre: term,
        });
        selectedGrupos.forEach((g) => params.append("grupoId", String(g.id)));
        if (estadoId !== "") {
          params.append("estadoId", String(estadoId));
          params.append("estado.id", String(estadoId)); // compat
        }

        // const res = await fetch(`${apiRoutes.clientes.list}?${params.toString()}`, { headers: authHeaders() });
        // if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // const data = await res.json();
        const data = await apiFetchJson(
          `${apiRoutes.clientes.list}?${params.toString()}`,
        ); // ✅

        const raw = data.items ?? [];
        const newItems = raw.map((c) => ({
          id: c.id,
          nombre: c.nombre,
          documento: c.documento,
          codigo: c.codigo,
        }));

        setNombreItems((prev) =>
          append
            ? dedupBy(
                [...prev, ...newItems],
                (i) => i.id ?? i.codigo ?? i.nombre ?? JSON.stringify(i),
              )
            : newItems,
        );
        setNombreOpen(true);

        const total = data.totalElements ?? null;
        const hasMore =
          total != null
            ? (page + 1) * PAGE_SIZE < total
            : newItems.length === PAGE_SIZE;
        setNombreHasMore(hasMore);
        setNombrePage(page);
      } catch (e) {
        setError(e.message);
      } finally {
        setNombreLoading(false);
      }
    },
    [selectedGrupos, estadoId],
  );

  const fetchDocumentoPage = useCallback(
    async (term, page, append = false) => {
      try {
        setDocumentoLoading(true);
        const params = new URLSearchParams({
          page,
          size: PAGE_SIZE,
          documento: term,
        });
        selectedGrupos.forEach((g) => params.append("grupoId", String(g.id)));
        if (estadoId !== "") {
          params.append("estadoId", String(estadoId));
          params.append("estado.id", String(estadoId)); // compat
        }

        // const res = await fetch(`${apiRoutes.clientes.list}?${params.toString()}`, { headers: authHeaders() });
        // if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // const data = await res.json();
        const data = await apiFetchJson(
          `${apiRoutes.clientes.list}?${params.toString()}`,
        ); // ✅

        const raw = data.items ?? [];
        const newItems = raw.map((c) => ({
          id: c.id,
          nombre: c.nombre,
          documento: c.documento,
          codigo: c.codigo,
        }));

        setDocumentoItems((prev) =>
          append
            ? dedupBy(
                [...prev, ...newItems],
                (i) => i.id ?? i.codigo ?? i.documento ?? JSON.stringify(i),
              )
            : newItems,
        );
        setDocumentoOpen(true);

        const total = data.totalElements ?? null;
        const hasMore =
          total != null
            ? (page + 1) * PAGE_SIZE < total
            : newItems.length === PAGE_SIZE;
        setDocumentoHasMore(hasMore);
        setDocumentoPage(page);
      } catch (e) {
        setError(e.message);
      } finally {
        setDocumentoLoading(false);
      }
    },
    [selectedGrupos, estadoId],
  );

  // Cod. Cliente: vía /clientes/autocomplete?tipo=codigo
  const fetchCodigoClientePage = useCallback(
    async (term, page, append = false) => {
      try {
        setCodigoClienteLoading(true);

        const params = new URLSearchParams({
          q: term,
          page,
          size: PAGE_SIZE,
          tipo: "codigo",
        });
        // múltiples grupoId
        selectedGrupos.forEach((g) => params.append("grupoId", String(g.id)));
        if (estadoId !== "") params.append("estadoId", String(estadoId));

        const url = `${apiRoutes.clientes.autocomplete}?${params.toString()}`;
        // const res = await fetch(url, { headers: authHeaders() });
        // if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // const data = await res.json();
        const data = await apiFetchJson(url); // ✅

        // AutoPageDto => { items: [...], page, size, hasMore }
        const items = Array.isArray(data?.items) ? data.items : [];
        const newItems = items.map((c) => ({
          codigo: c.codigo,
          nombre: c.nombre,
        }));

        setCodigoClienteItems((prev) =>
          append
            ? dedupBy(
                [...prev, ...newItems],
                (i) => i.codigo ?? JSON.stringify(i),
              )
            : newItems,
        );
        setCodigoClienteOpen(true);

        setCodigoHasMore(Boolean(data?.hasMore));
        setCodigoPage(page);
      } catch (e) {
        setError(e.message);
      } finally {
        setCodigoClienteLoading(false);
      }
    },
    [selectedGrupos, estadoId],
  );

  // ---------- /clientes/grupos (autocomplete + scroll infinito) ----------
  const gruposBase = apiRoutes?.clientes?.grupos;

  const fetchGrupos = useCallback(
    async (term, page = 0, append = false) => {
      if (!gruposBase) return;
      try {
        setGrupoLoading(true);
        setErrorGrupos("");
        const p = new URLSearchParams();
        p.append("page", String(page));
        p.append("size", String(PAGE_SIZE));
        p.append("sort", "nombre,DESC");
        if (term && term.trim() !== "") p.append("q", term.trim());

        // const res = await fetch(`${gruposBase}?${p.toString()}`, { headers: authHeaders() });
        // if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // const data = await res.json();
        const data = await apiFetchJson(`${gruposBase}?${p.toString()}`); // ✅

        const raw = Array.isArray(data) ? data : (data.items ?? []);
        const newItems = raw.map((g) => ({
          id: g.id,
          nombre: g.nombre ?? g.GRCL_NM_GRUPO ?? `Grupo ${g.id}`,
        }));

        setGrupoItems((prev) => {
          const merged = append
            ? dedupBy([...prev, ...newItems], (i) => String(i.id))
            : newItems;
          return merged;
        });

        const hasMore = Array.isArray(data) ? false : !!data.hasMore;
        setGrupoHasMore(hasMore);
        setGrupoPage(page);
        setGrupoOpen(true);
      } catch (e) {
        setErrorGrupos("No fue posible cargar los grupos.");
        setGrupoOpen(false);
      } finally {
        setGrupoLoading(false);
      }
    },
    [gruposBase],
  );

  const handleGrupoFocus = async () => {
    closeDropdowns("grupo"); // 👈 cerramos los demás
    const term = (grupoQ || "").trim();
    if (term === "") {
      await fetchGrupos("", 0, false);
    } else {
      await fetchGrupos(term, 0, false);
    }
  };

  const onGrupoChange = async (value) => {
    setGrupoQ(value);
    closeDropdowns("grupo"); // mantener solo el de grupos abierto mientras escribo ahí
    const term = value.trim();
    if (term === "") {
      await fetchGrupos("", 0, false);
    } else {
      await fetchGrupos(term, 0, false);
    }
  };

  const handleGrupoScroll = (e) => {
    if (!grupoHasMore || grupoLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchGrupos(grupoQ.trim(), grupoPage + 1, true);
    }
  };

  // ⚠️ Al seleccionar/quitar grupo: NO emitimos onChangeFilters (se aplica recién al Submit)
  const onSelectGrupo = (item) => {
    if (!selectedGrupos.some((g) => String(g.id) === String(item.id))) {
      const next = [...selectedGrupos, item];
      setSelectedGrupos(next);
    }
    setGrupoQ("");
    setGrupoOpen(false);
  };

  const onRemoveGrupo = (id) => {
    const next = selectedGrupos.filter((g) => String(g.id) !== String(id));
    setSelectedGrupos(next);
    // (sin emisión en vivo)
  };

  // ---------- Cargar ESTADOS de Cliente al montar ----------
  const devEstadosRunRef = useRef(false); // evita doble GET en dev (StrictMode)
  useEffect(() => {
    if (process.env.NODE_ENV !== "production") {
      if (!devEstadosRunRef.current) {
        devEstadosRunRef.current = true; // primera pasada en dev → no ejecutar aún
        return;
      }
    }
    const loadEstados = async () => {
      try {
        const url = apiRoutes?.clientes?.estados;
        if (!url) return;
        // const res = await fetch(url, { headers: authHeaders() });
        // if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // const data = await res.json(); // [{id, nombre}]
        const data = await apiFetchJson(url); // ✅
        const opts = [
          { id: "", label: "Todos" },
          ...(Array.isArray(data)
            ? data.map((e) => ({ id: String(e.id), label: e.nombre }))
            : []),
        ];
        setEstados(opts);
      } catch (e) {
        setErrorEstados("No fue posible cargar los estados.");
      }
    };
    loadEstados();
  }, []);

  // ---------- Debounce (incluyen grupos/estado como deps para reconsultar SUGERENCIAS) ----------
  useEffect(() => {
    const t = setTimeout(() => {
      if (nombreQ.length >= 2) {
        setNombreItems([]);
        setNombrePage(0);
        setNombreHasMore(false);
        fetchNombrePage(nombreQ, 0, false);
      } else {
        setNombreItems([]);
        setNombreOpen(false);
        setNombrePage(0);
        setNombreHasMore(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [nombreQ, selectedGrupos, estadoId, fetchNombrePage]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (documentoQ.length >= 2) {
        setDocumentoItems([]);
        setDocumentoPage(0);
        setDocumentoHasMore(false);
        fetchDocumentoPage(documentoQ, 0, false);
      } else {
        setDocumentoItems([]);
        setDocumentoOpen(false);
        setDocumentoPage(0);
        setDocumentoHasMore(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [documentoQ, selectedGrupos, estadoId, fetchDocumentoPage]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (codigoClienteQ.length >= 2) {
        setCodigoClienteItems([]);
        setCodigoPage(0);
        setCodigoHasMore(false);
        fetchCodigoClientePage(codigoClienteQ, 0, false);
      } else {
        setCodigoClienteItems([]);
        setCodigoClienteOpen(false);
        setCodigoPage(0);
        setCodigoHasMore(false);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [codigoClienteQ, selectedGrupos, estadoId, fetchCodigoClientePage]);

  // ---------- Scroll handlers (infinite) ----------
  const handleNombreScroll = (e) => {
    if (!nombreHasMore || nombreLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchNombrePage(nombreQ, nombrePage + 1, true);
    }
  };
  const handleDocumentoScroll = (e) => {
    if (!documentoHasMore || documentoLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchDocumentoPage(documentoQ, documentoPage + 1, true);
    }
  };
  const handleCodigoScroll = (e) => {
    if (!codigoHasMore || codigoClienteLoading) return;
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) {
      fetchCodigoClientePage(codigoClienteQ, codigoPage + 1, true);
    }
  };

  // ---------- Handlers selección ----------
  const onSelectNombre = (item) => {
    if (!selectedNombres.some((i) => i.id === item.id)) {
      const next = [...selectedNombres, item];
      setSelectedNombres(next);
    }
    setNombreQ("");
    setNombreOpen(false);
  };
  const onRemoveNombre = (id) => {
    const next = selectedNombres.filter((i) => i.id !== id);
    setSelectedNombres(next);
    emitAllFrom(next, selectedDocumentos, selectedCodigos); // sin grupos
  };

  const onSelectDocumento = (item) => {
    if (!selectedDocumentos.some((i) => i.id === item.id)) {
      const next = [...selectedDocumentos, item];
      setSelectedDocumentos(next);
    }
    setDocumentoQ("");
    setDocumentoOpen(false);
  };
  const onRemoveDocumento = (id) => {
    const next = selectedDocumentos.filter((i) => i.id !== id);
    setSelectedDocumentos(next);
    emitAllFrom(selectedNombres, next, selectedCodigos); // sin grupos
  };

  const onSelectCodigoCliente = (item) => {
    if (!selectedCodigos.some((i) => i.codigo === item.codigo)) {
      const next = [...selectedCodigos, item];
      setSelectedCodigos(next);
    }
    setCodigoClienteQ("");
    setCodigoClienteOpen(false);
  };
  const onRemoveCodigo = (codigo) => {
    const next = selectedCodigos.filter((i) => i.codigo !== codigo);
    setSelectedCodigos(next);
    emitAllFrom(selectedNombres, selectedDocumentos, next); // sin grupos
  };

  // ---------- Submit (botón "Aplicar Filtros") ----------
  const handleSubmit = (e) => {
    e.preventDefault();
    const snapshot = buildSnapshot(
      selectedNombres,
      selectedDocumentos,
      selectedCodigos,
      selectedGrupos,
    );

    if (
      snapshot.codigosCliente.length === 0 &&
      snapshot.codigosExtra.length === 0
    ) {
      if (nombreQ.trim()) snapshot.nombre = nombreQ.trim();
      if (documentoQ.trim()) snapshot.documento = documentoQ.trim();
    }

    if (estadoId !== "") snapshot.estadoId = estadoId;

    onSubmit([], snapshot);
  };

  return (
    <div className="drawer-backdrop">
      <div
        ref={filtroRef}
        onClick={(e) => e.stopPropagation()}
        className="drawer-panel overflow-auto"
      >
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button 
          onClick={onClose} 
          className="btn-cancel" 
          title="Cerrar">
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="drawer-body">
          {/* Nombre */}
          <div style={{ position: "relative" }}>
            <label htmlFor="nombreFiltro" className="form-label-fil">
              Nombre
            </label>
            <input
              id="nombreFiltro"
              type="text"
              placeholder="Escribí al menos 2 caracteres..."
              value={nombreQ}
              onChange={(e) => {
                setNombreQ(e.target.value);
                closeDropdowns("nombre");
              }}
              className="input"
              autoComplete="off"
              onFocus={() => {
                if (nombreItems.length) setNombreOpen(true);
                closeDropdowns("nombre");
              }}
              onKeyDown={handleKeyDown}
            />
            <div className="chips">
              {selectedNombres.map((item) => (
                <span key={item.id} className="inline-flex items-center gap-1">
                  {item.nombre}
                  <button type="button" onClick={() => onRemoveNombre(item.id)}>
                    &times;
                  </button>
                </span>
              ))}
            </div>
            {nombreOpen && (
              <div
                onScroll={handleNombreScroll}
                className="dropdown-panel max-h-60"
              >
                {nombreItems.map((item) => (
                  <div
                    key={item.id ?? `${item.codigo}-${item.nombre}`}
                    onClick={() => onSelectNombre(item)}
                    className="p-2 cursor-pointer hover:bg-gray-200"
                  >
                    {item.nombre} {item.documento && `(${item.documento})`}
                  </div>
                ))}
                {nombreLoading && (
                  <div className="p-2 text-sm text-gray-500">Cargando...</div>
                )}
                {!nombreLoading && nombreHasMore && (
                  <div className="dropdown-item text-gray-400 text-center text-xs">
                    Desplazá para ver más…
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Documento */}
          <div style={{ position: "relative" }}>
            <label htmlFor="documentoFiltro" className="form-label-fil">
              Documento
            </label>
            <input
              id="documentoFiltro"
              type="text"
              placeholder="Escribí al menos 2 caracteres..."
              value={documentoQ}
              onChange={(e) => {
                setDocumentoQ(e.target.value);
                closeDropdowns("documento");
              }}
              className="input"
              autoComplete="off"
              onFocus={() => {
                if (documentoItems.length) setDocumentoOpen(true);
                closeDropdowns("documento");
              }}
              onKeyDown={handleKeyDown}
            />
            <div className="chips">
              {selectedDocumentos.map((item) => (
                <span key={item.id} className="inline-flex items-center gap-1">
                  {item.documento}
                  <button
                    type="button"
                    onClick={() => onRemoveDocumento(item.id)}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
            {documentoOpen && (
              <div
                onScroll={handleDocumentoScroll}
                className="dropdown-panel max-h-60"
              >
                {documentoItems.map((item) => (
                  <div
                    key={item.id ?? `${item.codigo}-${item.documento}`}
                    onClick={() => onSelectDocumento(item)}
                    className="dropdown-item"
                  >
                    {item.documento} {item.nombre && `(${item.nombre})`}
                  </div>
                ))}
                {documentoLoading && (
                  <div className="dropdown-item text-gray-500">Cargando...</div>
                )}
                {!documentoLoading && documentoHasMore && (
                  <div className="dropdown-item text-gray-400 text-center text-xs">
                    Desplazá para ver más…
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Grupo Cliente — AUTOCOMPLETE + chips (aplica recién al Submit) 
          <div style={{ position: "relative" }}>
            <label className="form-label-fil">Grupo Cliente</label>
            <input
              type="text"
              placeholder="Enfocá para ver los primeros… o escribí para filtrar"
              value={grupoQ}
              onChange={(e) => onGrupoChange(e.target.value)}
              className="input"
              autoComplete="off"
              onFocus={handleGrupoFocus}
              onKeyDown={handleKeyDown}
            />
            <div className="chips">
              {selectedGrupos.map((item) => (
                <span key={item.id} className="inline-flex items-center gap-1">
                  {item.nombre}
                  <button type="button" onClick={() => onRemoveGrupo(item.id)}>
                    &times;
                  </button>
                </span>
              ))}
            </div>
            {grupoOpen && (
              <div
                onScroll={handleGrupoScroll}
                className="dropdown-panel max-h-60"
              >
                {grupoItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => onSelectGrupo(item)}
                    className="dropdown-item"
                  >
                    {item.nombre}
                  </div>
                ))}
                {grupoLoading && (
                  <div className="dropdown-item text-gray-500">Cargando...</div>
                )}
                {!grupoLoading && grupoItems.length === 0 && (
                  <div className="dropdown-item text-gray-500">
                    Sin coincidencias
                  </div>
                )}
                {!grupoLoading && grupoHasMore && (
                  <div className="dropdown-item text-gray-400 text-center text-xs">
                    Desplazá para ver más…
                  </div>
                )}
              </div>
            )}
            {errorGrupos && (
              <div className="text-sm text-red-600 mt-1">{errorGrupos}</div>
            )}
          </div>*/}

          {/* Cod. Cliente (MULTI) */}
          <div style={{ position: "relative" }}>
            <label htmlFor="codClienteFiltro" className="form-label-fil">
              Cód. Cliente
            </label>
            <input
              id="codClienteFiltro"
              type="text"
              value={codigoClienteQ}
              onChange={(e) => {
                setCodigoClienteQ(e.target.value);
                closeDropdowns("codigo");
              }}
              className="input"
              autoComplete="off"
              placeholder="Escribí al menos 2 caracteres..."
              onFocus={() => {
                if (codigoClienteItems.length) setCodigoClienteOpen(true);
                closeDropdowns("codigo");
              }}
              onKeyDown={handleKeyDown}
            />
            <div className="chips">
              {selectedCodigos.map((item) => (
                <span
                  key={item.codigo}
                  className="inline-flex items-center gap-1"
                >
                  {item.codigo}
                  {item.nombre ? ` (${item.nombre})` : ""}
                  <button
                    type="button"
                    onClick={() => onRemoveCodigo(item.codigo)}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
            {codigoClienteOpen && (
              <div
                onScroll={handleCodigoScroll}
                className="dropdown-panel max-h-60"
              >
                {codigoClienteItems.map((item) => (
                  <div
                    key={item.codigo}
                    onClick={() => onSelectCodigoCliente(item)}
                    className="dropdown-item"
                  >
                    {item.codigo} {item.nombre && `(${item.nombre})`}
                  </div>
                ))}
                {codigoClienteLoading && (
                  <div className="dropdown-item text-gray-500">Cargando...</div>
                )}
                {!codigoClienteLoading && codigoHasMore && (
                  <div className="dropdown-item text-gray-400 text-center text-xs">
                    Desplazá para ver más…
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Estado */}
          <div>
            <label htmlFor="estadoFiltro" className="form-label-fil">
              Estado
            </label>
            <select
              id="estadoFiltro"
              value={estadoId}
              onChange={(e) => {
                setEstadoId(e.target.value);
                closeDropdowns(null);
              }}
              className="input"
              onKeyDown={handleKeyDown}
              onFocus={() => closeDropdowns(null)}
            >
              {estados.map((op) => (
                <option key={op.id} value={op.id}>
                  {op.label}
                </option>
              ))}
            </select>
            {errorEstados && (
              <div className="mt-1 text-sm text-red-600">{errorEstados}</div>
            )}
          </div>

          <div className="text-right">
            <button type="submit" className="btn">
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
